import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ScannerMode from './components/ScannerMode';
import TabletMode from './components/TabletMode';
import ManualMode from './components/ManualMode';
import BadgePreview from './components/BadgePreview';
import ConfirmDialog from './components/ConfirmDialog';
import LabelPreview from './components/LabelPreview';
import DfpLogo from './components/DfpLogo';
import { api } from './services/api';
import { storage } from './services/storage';

function App() {
    const [mode, setMode] = useState('scanner'); // 'scanner', 'tablet', 'manual'
    const [selectedEvent, setSelectedEvent] = useState('');
    const [events, setEvents] = useState([]);
    const [currentRegistration, setCurrentRegistration] = useState(null);
    const [message, setMessage] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [showEventSelector, setShowEventSelector] = useState(false);
    const [pendingRegistrations, setPendingRegistrations] = useState([]);
    const [lastManualSearch, setLastManualSearch] = useState({ term: '', results: [] });
    const [showLabelPreview, setShowLabelPreview] = useState(false);

    // Load events on mount
    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            const eventList = await api.getEvents();
            setEvents(eventList);
            
            // Restore previously selected event from localStorage
            const savedEventId = storage.getSelectedEvent();
            if (savedEventId && eventList.some(e => e.id === savedEventId)) {
                setSelectedEvent(savedEventId);
            }
        } catch (error) {
            console.error('Failed to load events:', error);
            setMessage({ type: 'error', text: 'Failed to load events: ' + error.message });
        }
    };

    const handleEventChange = (eventId) => {
        setSelectedEvent(eventId);
        storage.saveSelectedEvent(eventId);
        setCurrentRegistration(null);
        setMessage(null);
    };

    const handleScanResult = async (qrCode) => {
        if (!selectedEvent) {
            setMessage({ type: 'error', text: 'Please select an event first' });
            return;
        }

        setMessage({ type: 'info', text: 'Looking up registration...' });

        try {
            const result = await api.searchByQR(qrCode);
            
            // Filter results to only include the selected event
            let filteredResults = [];
            
            if (Array.isArray(result)) {
                filteredResults = result.filter(reg => reg.eventId === selectedEvent);
            } else if (result.multiple && result.registrations && result.registrations.length > 0) {
                filteredResults = result.registrations.filter(reg => reg.eventId === selectedEvent);
            } else if (result.eventId === selectedEvent) {
                filteredResults = [result];
            }
            
            if (filteredResults.length === 0) {
                setMessage({ type: 'error', text: 'No registrations found for this event' });
            } else if (filteredResults.length === 1) {
                handleRegistrationFound(filteredResults[0]);
            } else {
                // Multiple registrations for selected event
                setPendingRegistrations(filteredResults);
                setShowEventSelector(true);
                setMessage(null);
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message || 'Registration not found' });
        }
    };

    const handleRegistrationFound = (registration) => {
        setMessage(null);
        
        // Check if already printed today
        const printed = storage.wasPrintedToday(registration.qrCode);
        
        if (printed) {
            setPendingRegistration(registration);
            setShowConfirm(true);
        } else {
            setCurrentRegistration(registration);
        }
    };

    const handleConfirmPrint = () => {
        setShowConfirm(false);
        setCurrentRegistration(pendingRegistration);
        setPendingRegistration(null);
    };

    const handleCancelPrint = () => {
        setShowConfirm(false);
        setPendingRegistration(null);
        setMessage({ type: 'info', text: 'Print cancelled' });
    };

    const handlePrintComplete = (registration) => {
        const count = storage.markAsPrinted(registration.qrCode, registration.name);
        setMessage({ 
            type: 'success', 
            text: `✅ Badge printed successfully for ${registration.name}` + (count > 1 ? ` (${count} times today)` : '')
        });
        setCurrentRegistration(null);
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(null), 3000);
    };

    const handleCancel = () => {
        setCurrentRegistration(null);
        setMessage(null);
        // Manual mode will restore search when remounted
    };

    const handleSaveSearchState = useCallback((searchTerm, results) => {
        setLastManualSearch({ term: searchTerm, results });
    }, []);

    const handleSelectRegistrationFromList = (registration) => {
        handleRegistrationFound(registration);
        setShowEventSelector(false);
        setPendingRegistrations([]);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setCurrentRegistration(null);
        setMessage(null);
    };

    return (
        <div className="App">
            <header className="header">
                <div className="header-content">
                    <div className="header-brand">
                        <DfpLogo className="header-logo" />
                        <span className="header-divider" aria-hidden="true"></span>
                        <h1>Badge Scanner</h1>
                    </div>
                    <button 
                        className="label-preview-btn"
                        onClick={() => setShowLabelPreview(!showLabelPreview)}
                        title="Toggle Label Preview (Debug)"
                        style={{ display: 'none' }}
                    >
                        📐
                    </button>
                </div>
            </header>

            <main className="main-content">
                {!currentRegistration && (
                    <>
                        <div className="event-selector-container">
                            <label htmlFor="event-select">Select Event:</label>
                            <select
                                id="event-select"
                                value={selectedEvent}
                                onChange={(e) => handleEventChange(e.target.value)}
                                className="event-dropdown"
                            >
                                <option value="">-- Choose an event --</option>
                                {events.map(event => (
                                    <option key={event.id} value={event.id}>
                                        {event.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mode-selector">
                            <button 
                                className={`mode-btn ${mode === 'scanner' ? 'active' : ''}`}
                                onClick={() => handleModeChange('scanner')}
                            >
                                Scanner
                            </button>
                            <button 
                                className={`mode-btn ${mode === 'tablet' ? 'active' : ''}`}
                                onClick={() => handleModeChange('tablet')}
                            >
                                Tablet
                            </button>
                            <button 
                                className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
                                onClick={() => handleModeChange('manual')}
                            >
                                Manual
                            </button>
                        </div>

                        {message && (
                            <div className={`message ${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="scanner-container">
                            {mode === 'scanner' && (
                                <ScannerMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'tablet' && (
                                <TabletMode 
                                    onScanResult={handleScanResult}
                                    eventId={selectedEvent}
                                />
                            )}
                            {mode === 'manual' && (
                                <ManualMode 
                                    onSelectRegistration={handleRegistrationFound}
                                    eventId={selectedEvent}
                                    onSaveSearchState={handleSaveSearchState}
                                    initialSearchState={lastManualSearch}
                                />
                            )}
                        </div>
                    </>
                )}

                {currentRegistration && (
                    <BadgePreview 
                        registration={currentRegistration}
                        onPrintComplete={handlePrintComplete}
                        onCancel={handleCancel}
                    />
                )}
            </main>

            {showConfirm && pendingRegistration && (
                <ConfirmDialog 
                    message={`Badge for ${pendingRegistration.name} has already been printed ${storage.wasPrintedToday(pendingRegistration.qrCode).count} time(s) today. Print again?`}
                    onConfirm={handleConfirmPrint}
                    onCancel={handleCancelPrint}
                />
            )}

            {showEventSelector && pendingRegistrations.length > 1 && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Multiple Registrations Found</h3>
                        <p>This contact is registered for multiple events. Please select which event to print the badge for:</p>
                        <div className="results-list">
                            {pendingRegistrations.map((reg, index) => (
                                <div key={reg.qrCode || reg.id || index} className="result-item">
                                    <div className="result-info">
                                        <h3>{reg.name}</h3>
                                        <p>{reg.eventName || 'Event'}</p>
                                        <p style={{ fontSize: '0.8rem', color: '#999' }}>
                                            {reg.title} at {reg.company}
                                        </p>
                                    </div>
                                    <button 
                                        className="select-btn"
                                        onClick={() => handleSelectRegistrationFromList(reg)}
                                    >
                                        Select
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <button 
                                className="modal-btn secondary"
                                onClick={() => {
                                    setShowEventSelector(false);
                                    setPendingRegistrations([]);
                                    setMessage(null);
                                }}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLabelPreview && (
                <div className="label-preview-modal">
                    <div className="label-preview-modal-content">
                        <button 
                            className="modal-close-btn"
                            onClick={() => setShowLabelPreview(false)}
                        >
                            ✕
                        </button>
                        <LabelPreview />
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
